import {gql, useMutation} from '@apollo/client';
import {Pda} from './components/Pda';
import {Header} from './components/Header';
import {isBagNumber} from '../../utils/BarCodeUtil';
import {NavBar, Switch, Popup, Dialog} from "antd-mobile";
import {ArrowLeftOutlined, SettingOutlined} from "@ant-design/icons";
import React, { useState, useRef } from "react";
import { t } from '../../i18n';
import { getNavBarContainerStyle } from '../../utils/deviceUtils';
import BluetoothScaleSelector from './components/BluetoothScaleSelector';
import { useBluetoothScaleStore } from '../../store/bluetoothScaleStore';
import {ArrivalScanWightComponent} from "./components/ArrivalScanWeightComponent";
import { showWarning } from '../../utils/newExceptionUtils';
import {playScanFailedAoSound} from "../../utils/scanUtils";

// 定义响应接口
interface ArrivalScanResponse {
    success: boolean;
    messageKeyStr?: string;
}

interface HandleArrivalScanEventResult {
    handleArrivalScanEvent: ArrivalScanResponse;
}

export interface HandleScanEventResult {
    success: boolean;
    messageKeyStr?: string;
}

const HANDLE_ARRIVAL_SCAN_EVENT = gql`
   mutation HandleArrivalScanEvent($barCode: String!, $weightKg: Float) {
     handleArrivalScanEvent(barCode: $barCode, weightKg: $weightKg) {
       success
       messageKeyStr
     }
   }
`

interface PageBodyProps {
    showSettings: boolean;
    setShowSettings: (show: boolean) => void;
    onScanSuccess: () => void;
}

const PageBody = ({ showSettings, setShowSettings, onScanSuccess }: PageBodyProps) => {
    const [handleArrivalScanEvent] = useMutation<HandleArrivalScanEventResult>(HANDLE_ARRIVAL_SCAN_EVENT);

    // 使用全局蓝牙状态
    const { isConnected: isBluetoothConnected, currentWeightData, weighSimultaneously, setWeighSimultaneously } = useBluetoothScaleStore();

    // 根据 messageKeyStr 获取国际化消息
    const getLocalizedMessage = (messageKeyStr: string): string => {
        // 后端返回的就是完整的国际化路径，直接使用 t() 函数
        return t(messageKeyStr, messageKeyStr);
    };

    const handleScanEvent = async (barCode: string): Promise<HandleScanEventResult> => {
        let newWeighSimultaneously = weighSimultaneously
        // 如果开启了同时称重，需要校验蓝牙连接和重量数据
        let weightKg: number | undefined = undefined;
        //不过当前为包牌，不做重量处理
        if (isBagNumber(barCode)) {
            newWeighSimultaneously = false
            weightKg = undefined
        }
        if (newWeighSimultaneously) {
            if (!isBluetoothConnected) {
                // 弹窗提示未连接蓝牙，不抛出异常
                Dialog.alert({
                    content: t('pda.pages.arrival.bluetoothNotConnected'),
                    confirmText: t('pda.common.confirm'),
                });
                return {success: false};
            }
            if (!currentWeightData || currentWeightData.weight <= 0) {
                // 弹窗提示无法获取重量
                Dialog.alert({
                    content: t('pda.pages.arrival.weightNotAvailable'),
                    confirmText: t('pda.common.confirm'),
                });
                return {success: false};
            }

            // 检查 rawData 的第一个字符是否为负号
            if (currentWeightData.rawData && currentWeightData.rawData.charAt(0) === '-') {
                // 弹窗提示重量为负数
                Dialog.alert({
                    content: t('pda.pages.arrival.negativeWeightError'),
                    confirmText: t('pda.common.confirm'),
                });
                return {success: false};
            }

            // 单位转换：lb转kg，kg保持不变
            weightKg = currentWeightData.unit.toLowerCase() === 'lb'
                ? currentWeightData.weight * 0.453592
                : currentWeightData.weight;

            // 保留3位小数
            weightKg = Math.round(weightKg * 1000) / 1000;
        }

        const {data} = await handleArrivalScanEvent({
            variables: {
                barCode,
                weightKg: weightKg
            }
        });
        
        const result = data?.handleArrivalScanEvent;
        const response: HandleScanEventResult = {
            success: result?.success || false,
            messageKeyStr: result?.messageKeyStr
        };

        // 如果成功但有消息提示，显示警告
        if (result?.success && result?.messageKeyStr) {
            const localizedMessage = getLocalizedMessage(result.messageKeyStr);
            showWarning({
                message: localizedMessage
            });
        }
        
        // 如果扫描成功，触发页面级别的成功效果
        if (result?.success) {
            onScanSuccess();
        }
        
        return response;
    };


    return (
        <>
            {/* 设置弹出层 */}
            <Popup
                visible={showSettings}
                onMaskClick={() => setShowSettings(false)}
                position="right"
                bodyStyle={{ width: '100vw', height: '100vh', padding: 0 }}
            >
                <div className="w-full h-full flex flex-col">
                    {/* 设置标题栏 */}
                    <div className="flex items-center px-4 py-3 bg-white shadow-sm">
                        <div
                            className="cursor-pointer"
                            onClick={() => setShowSettings(false)}
                        >
                            <ArrowLeftOutlined className="text-xl" />
                        </div>
                        <h2 className="flex-1 text-center text-lg font-semibold">{t('pda.pages.arrival.settings')}</h2>
                        <div className="w-6"></div> {/* 占位平衡 */}
                    </div>

                    {/* 设置内容 - 与主页面相同的背景和间距 */}
                    <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
                        {/* 同时称重开关 */}
                        <div className="bg-white p-4 rounded">
                            <div className="flex justify-between items-center">
                                <span className="text-base font-medium">{t('pda.pages.arrival.weighSimultaneously')}</span>
                                <Switch
                                    checked={weighSimultaneously}
                                    onChange={setWeighSimultaneously}
                                />
                            </div>
                        </div>

                        {/* 蓝牙电子秤组件 - 始终显示，方便用户提前连接 */}
                        <BluetoothScaleSelector />
                    </div>
                </div>
            </Popup>

            {/* 扫描组件 */}
            <ArrivalScanWightComponent
                value1Placeholder={t('pda.pages.arrival.bagCodeTrackingNo')}
                value1ColumnTitle={t('pda.pages.arrival.bagCodeTrackingNo')}
                handleScanEvent={handleScanEvent}
                weighSimultaneously={weighSimultaneously}
                currentWeight={currentWeightData}
                isBluetoothConnected={isBluetoothConnected}
                successExtraFields={weighSimultaneously ? [{ title: t('pda.pages.arrival.weight'), key: 'weight' }] : undefined}
            />
        </>
    );
}

export default function ArrivalPage() {
    const [showSettings, setShowSettings] = useState(false);
    const [showSuccessFrame, setShowSuccessFrame] = useState(false);
    const successTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleScanSuccess = () => {
        // 清除之前的定时器
        if (successTimerRef.current) {
            clearTimeout(successTimerRef.current);
            successTimerRef.current = null;
        }
        
        // 显示绿框成功提示
        setShowSuccessFrame(true);
        
        // 设置1.5秒后自动隐藏绿框
        successTimerRef.current = setTimeout(() => {
            setShowSuccessFrame(false);
            successTimerRef.current = null;
        }, 1500);
    };

    // 组件卸载时清理定时器
    React.useEffect(() => {
        return () => {
            if (successTimerRef.current) {
                clearTimeout(successTimerRef.current);
            }
        };
    }, []);

    return (
        <div className="w-full h-full relative">
            {/* 绿色边框覆盖层 - 覆盖整个视口 */}
            {showSuccessFrame && (
                <div className="fixed inset-0 border-8 border-green-500 pointer-events-none transition-all duration-300 z-50 bg-green-50 bg-opacity-20"></div>
            )}
            
            {/* 内容区域 */}
            <div className="w-full h-full flex flex-col gap-2 p-2">
                {/* 顶部导航栏 */}
                <div
                    {...getNavBarContainerStyle('relative')}
                    style={{ ...(getNavBarContainerStyle().style || {}), paddingTop: 0 }} // ⬅️ 覆盖掉白条
                >
                    <Header title={t('pda.pages.arrival.arrivalScan')}/>
                    <div
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center cursor-pointer"
                        onClick={() => setShowSettings(true)}
                    >
                        <SettingOutlined className="text-xl text-gray-600" />
                    </div>
                </div>
                <Pda/>
                <PageBody 
                    showSettings={showSettings} 
                    setShowSettings={setShowSettings} 
                    onScanSuccess={handleScanSuccess}
                />
            </div>
        </div>
    );
}